import { observer } from "mobx-react";
import { isFailure } from "@/common/common";
import { lookupShareTypeName, ShareType } from "@/common/share";
import { AiPrompt } from "@/components-v2/AiPrompt";
import { Alert } from "@/components-v2/Alert";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { Button, LinkButton } from "@/components-v2/Button";
import { Checkbox } from "@/components-v2/Checkbox";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { MDateInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { Select } from "@/components-v2/Select";
import { Table } from "@/components-v2/Table";
import { Tabs } from "@/components-v2/Tabs";
import { MTextArea } from "@/components-v2/TextArea";
import { MTextInput } from "@/components-v2/TextInput";
import { showAlert } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import {
  useDispatch,
  useShareholdersStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import { useElementId } from "@/utils/reactHelpers";
import {
  chooseExerciseReceiver,
  closeExerciseDialog,
  continueToShareIssue,
  documentedExerciseBlocker,
  exerciseAmount,
  exerciseComplete,
  exerciseIssues,
  exerciseIssueSummary,
  exerciseProgramFilters,
  exerciseProjectName,
  exerciseProjectSources,
  ExerciseQuantity,
  exerciseQuantities,
  exerciseQuantityIcon,
  exerciseQuantityLabel,
  exerciseRequestDifferences,
  ExerciseRowState,
  ExerciseState,
  hasAcknowledgedExerciseRows,
  optionAttachmentUploadConfig,
  OptionGrantDetails,
  OptionPlan,
  prepExerciseMethodRadios,
  ProgramFilter,
  removeStagedExerciseFile,
  setAllExerciseRowsSelected,
  setExerciseAcknowledgedOnly,
  setExerciseEmployee,
  setExerciseProgram,
  setExerciseProjectName,
  setExerciseQuantity,
  setExerciseRowSelected,
  setExerciseShareType,
  setExerciseStep,
  stageExerciseFiles,
  submitDocumentedExercise,
  visibleExerciseRows,
} from "./optionsDomain";
import { companyHolderIds, InstrumentHolder } from "./instrumentHolders";
import {
  ExerciseReceiverChoice,
  holdingCompanyDetails,
  receiverChoiceId,
  receiverChoices,
  selectedReceiverChoice,
} from "./exerciseReceivers";

type ExerciseDialogProps = {
  exercise: ExerciseState;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  today: string;
};

export const RegisterExerciseDialog = observer(function RegisterExerciseDialog(
  props: ExerciseDialogProps,
) {
  const { exercise, grants, shareTypes, today } = props;
  const { orgNumber } = useUserStore();
  const { t, tf } = useTranslation();
  const dispatch = useDispatch();

  const firstRow = exercise.rows[0];
  const isComplete = exerciseComplete(exercise, grants, today);
  const isIssueStep = exercise.step === "issue";
  const isDocumented = exercise.method === "documented";
  const isCreatingProject = exercise.isCreatingProject ?? false;
  const canSubmit = isIssueStep
    ? isComplete && !(isDocumented && documentedExerciseBlocker(exercise) !== undefined)
    : isComplete;
  const submit = () =>
    dispatch(
      !canSubmit
        ? []
        : !isIssueStep
          ? [setExerciseStep("issue")]
          : isDocumented
            ? submitDocumentedExercise({
                orgNo: orgNumber,
                state: exercise,
                grants,
                shareTypes,
                today,
                t,
              })
            : continueToShareIssue(orgNumber, exercise, grants, today),
    );

  return (
    <Dialog
      title={
        exercise.isPreselected && firstRow !== undefined
          ? {
              key: "options.register-exercise-for",
              params: { name: `${firstRow.holderName} – ${tf(firstRow.program)}` },
            }
          : { key: "options.register-new-exercise" }
      }
      maxWidth={"xl"}
      onCancel={[closeExerciseDialog()]}
      isCancelDisabled={isCreatingProject}
      onSubmit={submit}
      subHeaderElement={
        <Tabs
          paddingX={"md"}
          tabs={[
            {
              id: "exercise",
              label: { key: "options.exercise" },
              isActive: !isIssueStep,
              onSelect: [setExerciseStep("exercise")],
              disabled: isCreatingProject,
            },
            {
              id: "issue",
              label: { key: "options.exercise-tab-issue" },
              isActive: isIssueStep,
              onSelect: [setExerciseStep("issue")],
              disabled: !isComplete || isCreatingProject,
            },
          ]}
        />
      }
      actions={
        <Box justifyContent={"end"} alignItems={"center"} gap={"xl"}>
          <LinkButton
            label={{ key: "cancel" }}
            disabled={isCreatingProject}
            onClick={[closeExerciseDialog()]}
          />
          {isIssueStep && (
            <LinkButton
              label={{ key: "back" }}
              disabled={isCreatingProject}
              onClick={[setExerciseStep("exercise")]}
            />
          )}
          <Button
            variant={"primary"}
            label={
              !isIssueStep
                ? { key: "next" }
                : isDocumented
                  ? { key: "dbot.create-project" }
                  : { key: "continue" }
            }
            arrow={isIssueStep ? undefined : "right"}
            disabled={!canSubmit}
            loading={isCreatingProject}
            onClick={submit}
          />
        </Box>
      }
    >
      {isIssueStep ? <IssueStep {...props} /> : <ExerciseStep {...props} />}
    </Dialog>
  );
});

const ExerciseStep = observer(function ExerciseStep({
  exercise,
  grants,
  plans,
  holders,
  shareTypes,
  today,
}: ExerciseDialogProps) {
  const { shareholders } = useShareholdersStore();
  const { t, tf, lang } = useTranslation();
  const dispatch = useDispatch();
  const companyHolders = companyHolderIds(holders, shareholders);
  const rows = visibleExerciseRows(exercise);
  const programFilters = exerciseProgramFilters(plans);

  const columns: TableColumn<ExerciseRowState>[] = [
    {
      name: "holder",
      label: { key: "instrument.holder" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.isSplit ? (
          <Box alignItems={"center"} gap={"sm"} paddingLeft={"xs"}>
            <Icon name={"arrow-elbow-down-right"} />
            <span>{t("options.exercise-holding-row", { name: row.holderName })}</span>
          </Box>
        ) : (
          <Box alignItems={"center"} gap={"md"}>
            <Checkbox
              checked={row.isSelected}
              onChange={(event) =>
                dispatch([setExerciseRowSelected(row.trancheId, event.target.checked)])
              }
            />
            <VBox gap={0}>
              <span>{row.holderName}</span>
              <small className={"subtle"}>{tf(row.program)}</small>
            </VBox>
          </Box>
        ),
    },
    {
      name: "quantity",
      label: { key: "options.exercise-quantity" },
      align: "left",
      notSorted: true,
      getValue: (row) => (
        <Select<ExerciseQuantity>
          compact
          value={row.quantity}
          items={exerciseQuantities}
          idFn={(quantity) => quantity}
          labelFn={exerciseQuantityLabel}
          iconFn={exerciseQuantityIcon}
          onSelect={(quantity) => [setExerciseQuantity(row.key, quantity)]}
        />
      ),
    },
    {
      name: "count",
      label: { key: "options.exercise-shares" },
      align: "left",
      notSorted: true,
      getValue: (row) => (
        <MTextInput
          model={row.count}
          compact
          align={"right"}
          disabled={row.quantity !== "MANUAL"}
          inputProps={{ className: "min-w-20" }}
          addon={
            row.shareTypeId === undefined ? undefined : (
              <span className={"subtle whitespace-nowrap px-2"}>
                {lookupShareTypeName(row.shareTypeId, shareTypes, lang)}
              </span>
            )
          }
        />
      ),
    },
    {
      name: "receiver",
      label: { key: "options.exercise-receiver" },
      align: "left",
      notSorted: true,
      getValue: (row) => <ExerciseReceiverSelect exercise={exercise} row={row} />,
    },
    ...(rows.some((row) => row.needsShareClass)
      ? [
          {
            name: "shareClass",
            label: { key: "options.options-for" },
            align: "left",
            notSorted: true,
            getValue: (row: ExerciseRowState) =>
              row.needsShareClass ? (
                <Select<ShareType>
                  compact
                  value={shareTypes.find((shareType) => shareType.sharetypeId === row.shareTypeId)}
                  items={shareTypes}
                  placeholder={t("options.exercise-select-share-class")}
                  idFn={(shareType) => String(shareType.sharetypeId)}
                  labelFn={(shareType) => ({
                    translated: lookupShareTypeName(shareType.sharetypeId, shareTypes, lang),
                  })}
                  onSelect={(shareType) => [setExerciseShareType(row.key, shareType.sharetypeId)]}
                />
              ) : null,
          } satisfies TableColumn<ExerciseRowState>,
        ]
      : []),
    {
      name: "cost",
      label: { key: "options.exercise-row-cost" },
      align: "left",
      notSorted: true,
      getValue: (row) => ({ type: "amount", value: exerciseAmount(row), currency: "NOK" }),
    },
    {
      name: "employee",
      label: { key: "options.exercise-employee" },
      align: "center",
      labelElement: (
        <Box alignItems={"center"} gap={"xs"}>
          <span className={"text-sm"}>{t("options.exercise-employee")}</span>
          <InfoButton infoText={{ key: "options.exercise-employee-help" }} />
        </Box>
      ),
      notSorted: true,
      getValue: (row) =>
        companyHolders.has(row.holderId) ? null : (
          <Checkbox
            checked={row.isEmployee}
            onChange={(event) => dispatch([setExerciseEmployee(row.key, event.target.checked)])}
          />
        ),
    },
  ];

  return (
    <VBox gap={"lg"} marginY={"sm"}>
      {!exercise.isPreselected && (
        <VBox gap={"md"}>
          <Box alignItems={"center"} gap={"lg"} wrap>
            <MTextInput
              model={exercise.search}
              name={"exercise_search"}
              placeholder={t("search")}
              icon={"magnifying-glass"}
              type={"search"}
              inline
            />
            <Select<ProgramFilter>
              grow={false}
              value={programFilters.find((filter) => filter.planId === exercise.planId)}
              items={programFilters}
              idFn={(filter) => filter.planId ?? "all"}
              labelFn={(filter) => filter.label}
              onSelect={(filter) => [setExerciseProgram(filter.planId)]}
            />
            <Checkbox
              checked={exercise.isAcknowledgedOnly}
              disabled={!hasAcknowledgedExerciseRows(exercise)}
              label={t("options.exercise-acknowledged-only")}
              onChange={(event) => dispatch([setExerciseAcknowledgedOnly(event.target.checked)])}
            />
          </Box>
          <Box alignItems={"center"} gap={"xs"}>
            <LinkButton
              label={{ key: "options.exercise-select-all" }}
              onClick={[setAllExerciseRowsSelected(exercise, true)]}
            />
            <span className={"color-primary"}>/</span>
            <LinkButton
              label={{ key: "none" }}
              onClick={[setAllExerciseRowsSelected(exercise, false)]}
            />
          </Box>
        </VBox>
      )}
      {isFailure(exercise.holdingCompaniesResult) && (
        <Alert
          type={"warning"}
          icon
          message={{ key: "options.exercise-holding-companies-failed" }}
        />
      )}
      {rows.length === 0 ? (
        <span className={"subtle"}>{t("options.exercise-no-rows")}</span>
      ) : (
        <DataTable tableColumns={columns} tableData={rows} getId={(row) => row.key} />
      )}
      <ExerciseIssueAlerts exercise={exercise} grants={grants} today={today} />
    </VBox>
  );
});

const ExerciseReceiverSelect = observer(function ExerciseReceiverSelect({
  exercise,
  row,
}: {
  exercise: ExerciseState;
  row: ExerciseRowState;
}) {
  const { tf } = useTranslation();
  const choices = receiverChoices(exercise, row);

  return (
    <Select<ExerciseReceiverChoice>
      compact
      disabled={choices.length < 2}
      value={selectedReceiverChoice(exercise, row)}
      items={choices}
      idFn={receiverChoiceId}
      labelFn={receiverChoiceLabel}
      iconFn={(choice) =>
        choice.type === "SELF"
          ? "user"
          : choice.type === "SELF_AND_HOLDING"
            ? "arrows-out-line-horizontal"
            : "buildings"
      }
      itemLabelFn={(choice) =>
        choice.type === "COMPANY" ? (
          <VBox gap={0}>
            <span>{choice.company.name}</span>
            <small className={"subtle"}>{tf(holdingCompanyDetails(choice.company))}</small>
          </VBox>
        ) : (
          receiverChoiceLabel(choice)
        )
      }
      onSelect={(choice) => [chooseExerciseReceiver(row.key, choice)]}
    />
  );
});

const receiverChoiceLabel = (choice: ExerciseReceiverChoice): TMessage => {
  switch (choice.type) {
    case "SELF":
      return { key: "options.exercise-receiver-self" };
    case "SELF_AND_HOLDING":
      return { key: "options.exercise-receiver-self-and-holding" };
    case "COMPANY":
      return { translated: choice.company.name };
  }
};

const IssueStep = observer(function IssueStep({ exercise, grants, today }: ExerciseDialogProps) {
  const { companyInfo } = useUserStore();
  const { t, tf } = useTranslation();
  const dispatch = useDispatch();
  const projectNameId = useElementId();
  const blocker = documentedExerciseBlocker(exercise);
  const stagedAttachments = exercise.stagedFiles.map((staged) => ({
    id: staged.id,
    filename: staged.file.name,
  }));

  return (
    <VBox gap={"xl"} marginY={"sm"}>
      <VBox gap={"md"}>
        <span>{t("options.exercise-issue-method")}</span>
        <RadioButtonGroup {...prepExerciseMethodRadios(exercise, dispatch)} />
        {blocker && <Alert type={"info"} icon message={blocker} />}
      </VBox>
      {companyInfo && (
        <Table variant={"COMPACT"}>
          <thead>
            <tr className={"text-left subtle"}>
              <th>{t("options.exercise-issue-summary")}</th>
              <th align={"right"}>{t("before")}</th>
              <th align={"right"}>{t("after")}</th>
            </tr>
          </thead>
          <tbody>
            {exerciseIssueSummary(exercise, companyInfo, "NOK").map((row, index) => (
              <tr key={index}>
                <td>{tf(row.label)}</td>
                <td align={"right"}>{tf(row.before)}</td>
                <td align={"right"}>{tf(row.after)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {exercise.method === "documented" ? (
        <VBox gap={"sm"}>
          <label htmlFor={projectNameId}>{t("dbot.new-project.title")}</label>
          <AiPrompt
            promptInputId={projectNameId}
            fileUploadConfig={optionAttachmentUploadConfig}
            prompt={exerciseProjectName(exercise, t)}
            promptPlaceholder={{ key: "dbot.new-project-name.placeholder" }}
            onPromptChange={(name) => dispatch([setExerciseProjectName(name)])}
            sources={exerciseProjectSources()}
            disabled={exercise.isCreatingProject}
          />
        </VBox>
      ) : (
        <>
          <Grid cols={1} colsMd={2} gap={"lg"}>
            <MDateInput label={t("options.exercise-date")} model={exercise.exerciseDate} />
          </Grid>
          <MTextArea label={t("remarks")} model={exercise.remarks} rows={3} />
          <VBox gap={"sm"}>
            <strong>{t("attachments")}</strong>
            <AttachmentsPanel
              title={false}
              fileUploadConfig={optionAttachmentUploadConfig}
              uploadedAttachments={stagedAttachments}
              onUpload={(files) => dispatch([stageExerciseFiles(files)])}
              onDelete={(id) => dispatch([removeStagedExerciseFile(id)])}
              onOpen={() => undefined}
              onInvalidFile={(message) => dispatch([showAlert(message, "error")])}
            />
          </VBox>
        </>
      )}
      <ExerciseIssueAlerts exercise={exercise} grants={grants} today={today} />
    </VBox>
  );
});

const ExerciseIssueAlerts = observer(function ExerciseIssueAlerts({
  exercise,
  grants,
  today,
}: {
  exercise: ExerciseState;
  grants: OptionGrantDetails[];
  today: string;
}) {
  return (
    <>
      {exerciseIssues(exercise, grants, today).map((issue, index) => (
        <Alert key={`issue-${index}`} type={"error"} icon message={issue} />
      ))}
      {exerciseRequestDifferences(exercise).map((difference, index) => (
        <Alert key={`difference-${index}`} type={"warning"} icon message={difference} />
      ))}
    </>
  );
});
